'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useClientCopy } from '@/lib/site-settings-context'
import { MediaImage as Image } from '@/components/media-image'
import { Sparkles } from '@/components/sparkles'

interface BeforeAfterProps {
  before: string
  after: string
  beforeAlt: string
  afterAlt: string
  className?: string
}

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov']
// Container is `max-w-6xl` (1152px) with px-4 (16px) padding, switching to a
// 2-column grid at lg (>=1024px). So actual width is viewport minus 32px on
// mobile, ~50% of the 6xl container above lg.
const MEDIA_SIZES = '(min-width: 1024px) min(560px, 50vw), calc(100vw - 32px)'

function isVideo(src: string): boolean {
  const lower = src.toLowerCase().split('?')[0] ?? ''
  return VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

/**
 * Convert a `/videos/foo.mp4` path to its companion poster `/images/posters/foo.webp`.
 * Companion posters are extracted once per video and committed alongside the source.
 * Returns null for sources outside the conventional `/videos/` layout.
 */
function videoPoster(src: string): string | null {
  const match = src.match(/^(.*)\/videos\/([^/?#]+)\.(?:mp4|webm|mov)(?:[?#].*)?$/i)
  if (!match) return null
  const [, prefix, basename] = match
  return `${prefix}/images/posters/${basename}.webp`
}

function LazyVideo({
  src,
  poster,
  alt,
  shouldLoad,
}: {
  src: string
  poster: string | undefined
  alt: string
  shouldLoad: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [canPlay, setCanPlay] = useState(false)

  useEffect(() => {
    if (!shouldLoad) return
    const video = videoRef.current
    if (!video) return

    const show = () => setCanPlay(true)
    video.addEventListener('canplaythrough', show)
    video.addEventListener('playing', show)
    video.play().catch(() => {})

    return () => {
      video.removeEventListener('canplaythrough', show)
      video.removeEventListener('playing', show)
    }
  }, [shouldLoad])

  return (
    <>
      {poster && (
        <Image
          src={poster}
          alt={alt}
          fill
          sizes={MEDIA_SIZES}
          quality={60}
          className={`object-cover transition-opacity duration-700 ${canPlay ? 'opacity-0' : 'opacity-100'}`}
        />
      )}
      {shouldLoad && (
        <video
          ref={videoRef}
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          aria-label={alt}
          className={`absolute inset-0 size-full object-cover transition-opacity duration-700 ${canPlay ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </>
  )
}

function Media({
  src,
  alt,
  shouldLoadVideo,
}: {
  src: string
  alt: string
  shouldLoadVideo: boolean
}) {
  if (isVideo(src)) {
    const poster = videoPoster(src) ?? undefined
    return <LazyVideo src={src} poster={poster} alt={alt} shouldLoad={shouldLoadVideo} />
  }
  return <Image src={src} alt={alt} fill sizes={MEDIA_SIZES} className="object-cover" />
}

export function BeforeAfter({
  before,
  after,
  beforeAlt,
  afterAlt,
  className = '',
}: BeforeAfterProps) {
  const { siteLabels } = useClientCopy()
  const containerRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState(50)
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const draggingRef = useRef(false)

  const rafRef = useRef(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadVideo(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const updatePosition = useCallback((clientX: number) => {
    const container = containerRef.current
    if (!container) return
    const rect = container.getBoundingClientRect()
    const x = clientX - rect.left
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100))
    setPosition(percent)
  }, [])

  // Throttle high-frequency pointer events to one update per frame
  const throttledUpdate = useCallback(
    (clientX: number) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0
        updatePosition(clientX)
      })
    },
    [updatePosition]
  )

  // Mouse drag (no scroll conflict on desktop)
  const startMouseDrag = useCallback(() => {
    if (draggingRef.current) return
    draggingRef.current = true

    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      throttledUpdate(e.clientX)
    }
    const stopDrag = () => {
      draggingRef.current = false
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', stopDrag)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', stopDrag)
  }, [throttledUpdate])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      startMouseDrag()
      updatePosition(e.clientX)
    },
    [startMouseDrag, updatePosition]
  )

  // Touch drag with direction lock: horizontal = slider, vertical = scroll
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return

      const startX = touch.clientX
      const startY = touch.clientY
      let locked: 'horizontal' | 'vertical' | null = null

      const onTouchMove = (ev: TouchEvent) => {
        const t = ev.touches[0]
        if (!t) return

        if (!locked) {
          const dx = Math.abs(t.clientX - startX)
          const dy = Math.abs(t.clientY - startY)
          // Need at least 4px of movement to decide direction
          if (dx < 4 && dy < 4) return
          locked = dx >= dy ? 'horizontal' : 'vertical'
          if (locked === 'horizontal') draggingRef.current = true
        }

        if (locked === 'horizontal') {
          ev.preventDefault()
          throttledUpdate(t.clientX)
        }
        // vertical: do nothing, browser scrolls naturally
      }

      const onTouchEnd = () => {
        draggingRef.current = false
        locked = null
        document.removeEventListener('touchmove', onTouchMove)
        document.removeEventListener('touchend', onTouchEnd)
      }

      document.addEventListener('touchmove', onTouchMove, { passive: false })
      document.addEventListener('touchend', onTouchEnd)
    },
    [throttledUpdate]
  )

  return (
    <div
      ref={containerRef}
      className={`relative aspect-[4/3] cursor-col-resize overflow-hidden rounded-sm select-none ${className}`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      role="slider"
      aria-label="Before and after comparison. Drag to reveal."
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(position)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') setPosition((p) => Math.max(0, p - 2))
        if (e.key === 'ArrowRight') setPosition((p) => Math.min(100, p + 2))
      }}
    >
      {/* After media (full, behind) */}
      <Media src={after} alt={afterAlt} shouldLoadVideo={shouldLoadVideo} />

      {/* Before media (clipped) */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}>
        <Media src={before} alt={beforeAlt} shouldLoadVideo={shouldLoadVideo} />
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 z-10 w-0.5 -translate-x-1/2 bg-white"
        style={{ left: `${position}%` }}
      />

      {/* Handle: lettuce icon (rock'd visible). Sparkles ride along with the
          icon so they only twinkle while the after / "rock'd" side is in view. */}
      <span
        aria-hidden
        className="pointer-events-none absolute top-1/2 z-20 block size-12 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-300"
        style={{ left: `${position}%`, opacity: position < 50 ? 1 : 0 }}
      >
        <Image
          src="/images/infernal-lettuce-icon.webp"
          alt=""
          width={48}
          height={48}
          aria-hidden
          className="pointer-events-none size-12 object-contain drop-shadow-[0_0_8px_rgba(149,248,98,0.6)]"
        />
        <Sparkles seed={12} colours={['#8bfa6e', '#00a134', '#e995e8', '#8a2585']} />
      </span>
      {/* Handle: arrow icon (before visible) */}
      <div
        className="absolute top-1/2 z-20 flex size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        style={{ left: `${position}%`, opacity: position < 50 ? 0 : 1 }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className="icon-rough text-white"
          aria-hidden="true"
        >
          <path
            d="M4 8L1 5M4 8L1 11M4 8H12M12 8L15 5M12 8L15 11"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Labels: fade out when slider covers them */}
      <span
        className="absolute top-3 left-3 z-10 rounded-sm bg-black/60 px-2.5 py-1 font-sans text-xs font-medium tracking-wider text-white/70 uppercase backdrop-blur-sm transition-opacity duration-200"
        style={{ opacity: position > 15 ? 1 : 0 }}
      >
        {siteLabels.beforeLabel}
      </span>
      <span
        className="absolute top-3 right-3 z-10 transition-opacity duration-200"
        style={{ opacity: position < 85 ? 1 : 0 }}
      >
        <span className="sticker-animate font-marker relative flex size-14 items-center justify-center rounded-full border-2 border-dashed border-black/20 bg-[#8bfa6e] text-base text-black shadow-lg sm:size-16 sm:text-lg">
          <Sparkles seed={22} colours={['#8a2585', '#e995e8', '#5b1b5a', '#00a134']} />
          <Sparkles seed={25} colours={['#8a2585', '#e995e8', '#5b1b5a', '#00a134']} />
          {siteLabels.afterLabel}
        </span>
      </span>
    </div>
  )
}
